import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box } from '@chakra-ui/react';

import formatPercent from 'common/formatPercent';
import Input, { InputProps } from 'components/_core/form/Input';
import SunshineDropdown from 'components/_core/SunshineDropdown';

type VatRateFieldProps<T extends FieldValues> = UseControllerProps<T> &
  InputProps;

const VAT_RATES = [0, 2.1, 5.5, 10, 20];

export const VatRateField = <T extends FieldValues>({
  control,
  defaultValue,
  name,
  rules,
  shouldUnregister,
  ...inputProps
}: VatRateFieldProps<T>) => {
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field }) => (
        <Box
          display="contents"
          sx={{
            '& [aria-haspopup="menu"]': {
              // Needed for the SunshineDropdown.Trigger, because we cannot right now pass props to the underlying component.
              // It has actually a fixed size, thus was preventing the underlying input to have the correct width
              width: '100%',
            },
          }}
        >
          <SunshineDropdown autoSelect={false} closeOnBlur>
            <SunshineDropdown.Trigger>
              <Input
                inputMode="numeric"
                name={name}
                rightIcon="chevron-down"
                step={0.01}
                type="number"
                {...inputProps}
                onBlur={field.onBlur}
                onChange={(e) => {
                  field.onChange(Number(e.target.value));
                }}
                ref={field.ref}
                value={field.value ?? ''}
              />
            </SunshineDropdown.Trigger>

            <SunshineDropdown.List>
              {VAT_RATES.map((rate) => (
                <SunshineDropdown.Item
                  key={rate}
                  onClick={() => {
                    field.onChange(rate);
                  }}
                >
                  {formatPercent(rate / 100)}
                </SunshineDropdown.Item>
              ))}
            </SunshineDropdown.List>
          </SunshineDropdown>
        </Box>
      )}
      rules={rules}
      shouldUnregister={shouldUnregister}
    />
  );
};
